import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';

interface MovieSectionProps {
  title: string;
  subtitle?: string;
  movies: Movie[];
  viewAllLink?: string;
  variant?: 'default' | 'featured';
}

export default function MovieSection({ title, subtitle, movies, viewAllLink, variant = 'default' }: MovieSectionProps) {
  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex items-end justify-between mb-7">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading">{title}</h2>
            {subtitle && (
              <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>
            )}
          </div>
          {viewAllLink && (
            <Link to={viewAllLink} className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          )}
        </div>
        {variant === 'featured' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {movies.map((movie, idx) => (
              <div key={movie.id} className={idx === 0 ? 'sm:col-span-2 sm:row-span-2' : ''}>
                <Link
                  to={`/article/${movie.id}`}
                  className="group block h-full cursor-pointer"
                >
                  <div className={`relative overflow-hidden rounded-lg bg-background-200 h-full ${idx === 0 ? 'aspect-auto' : 'aspect-[2/3]'}`}>
                    <img
                      src={movie.imageUrl}
                      alt={movie.title}
                      title={`${movie.title} - 6080影院电影大全`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                    {idx === 0 ? (
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded mb-2 inline-block whitespace-nowrap">
                          {movie.rating} 分
                        </span>
                        <h3 className="text-xl font-bold text-foreground-50 mt-2 mb-1 font-heading">{movie.title}</h3>
                        <p className="text-sm text-foreground-200 line-clamp-2">{movie.description}</p>
                      </div>
                    ) : (
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded whitespace-nowrap">
                          {movie.rating}
                        </span>
                        <h3 className="text-sm font-medium text-foreground-50 mt-2 font-heading">{movie.title}</h3>
                      </div>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {movies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}